import moment from 'moment-timezone'
import { useSelector } from 'react-redux'

/**
 * The school's local time: resolving its timezone and converting backend date-times to/from it.
 *
 * The backend stores and serves date-times (lesson start/end, schedule times, room bookings, ...)
 * as UTC instants, serialized as zoneless ISO strings (e.g. "2026-06-16T18:00:00") which by
 * contract mean UTC. The UI shows them in the SCHOOL's timezone (not the viewer's browser
 * timezone), so every user sees the same school-local wall clock.
 *
 * This module is the single place that deals with the school timezone:
 * - resolving it from school settings: {@link selectSchoolTimezone}, {@link useSchoolTimezone}
 * - read path (backend -> screen): {@link toSchoolTz}, {@link formatInSchoolTz}, {@link toWallClockDate}
 * - write path (user input -> backend): {@link toUtc}, {@link toUtcIso}
 */

// Used when the current school's timezone is not (yet) known on the client.
export const DEFAULT_SCHOOL_TIMEZONE = 'Europe/Kiev'

/**
 * Resolve the current school's IANA timezone (e.g. "Europe/Kiev") from auth state.
 * auth.schoolsWorksIn items come from /employees/school-name-list and carry { id, name, timezone }.
 * Falls back to {@link DEFAULT_SCHOOL_TIMEZONE} when the school or its timezone is not known yet.
 */
export const selectSchoolTimezone = (state) => {
  const { currentSchoolId, schoolsWorksIn } = state.auth || {}
  const school = Array.isArray(schoolsWorksIn)
    ? schoolsWorksIn.find((s) => s && s.id === currentSchoolId)
    : null
  return school?.timezone || DEFAULT_SCHOOL_TIMEZONE
}

/** Hook form of {@link selectSchoolTimezone} for use in components. */
export const useSchoolTimezone = () => useSelector(selectSchoolTimezone)

const resolveTz = (schoolTimezone) => schoolTimezone || DEFAULT_SCHOOL_TIMEZONE

// Parse a backend value as UTC. moment.utc treats a zoneless string as UTC (our contract) and
// also honours an explicit 'Z'/offset when present. Returns null for empty/invalid input.
const parseUtc = (value) => {
  if (value === null || value === undefined || value === '') return null
  const m = moment.utc(value)
  return m.isValid() ? m : null
}

/**
 * A backend UTC instant shifted into the school's timezone, as a moment object.
 * Use for comparisons or when several formats are needed from one value.
 * @param {string|Date|import('moment').Moment} value backend UTC date-time
 * @param {string} [schoolTimezone] IANA name, e.g. "Europe/Kiev"
 * @returns {import('moment').Moment|null}
 */
export const toSchoolTz = (value, schoolTimezone) => {
  const m = parseUtc(value)
  return m ? m.tz(resolveTz(schoolTimezone)) : null
}

/**
 * Format a backend UTC instant in the school's timezone.
 * @param {string|Date|import('moment').Moment} value backend UTC date-time
 * @param {string} [schoolTimezone] IANA name, e.g. "Europe/Kiev"
 * @param {string} [format] moment format (default "HH:mm")
 * @returns {string} formatted value, or "" for empty/invalid input
 */
export const formatInSchoolTz = (value, schoolTimezone, format = 'HH:mm') => {
  const m = toSchoolTz(value, schoolTimezone)
  return m ? m.format(format) : ''
}

/**
 * A backend UTC instant as a plain JS Date whose LOCAL fields carry the school-local wall clock.
 * Date/time pickers (react-datepicker, antd) render a Date in the browser's timezone, so to make
 * them show the school-local time we build a Date from the school wall-clock fields. The Date is
 * therefore display-only: it is NOT the original instant — convert back with {@link toUtc}.
 * @param {string|Date|import('moment').Moment} value backend UTC date-time
 * @param {string} [schoolTimezone] IANA name, e.g. "Europe/Kiev"
 * @returns {Date|null}
 */
export const toWallClockDate = (value, schoolTimezone) => {
  const m = toSchoolTz(value, schoolTimezone)
  return m ? new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes(), m.seconds(), 0) : null
}

/**
 * Inverse of {@link toSchoolTz}: parse a school-local wall-clock value (what the user typed/sees)
 * as a moment in UTC mode. Use on write paths so the round-trip is stable; format it with the
 * pattern each endpoint expects (ISO instant, zoneless LocalDateTime, HH:mm, ...).
 * @param {string} localValue wall-clock value in the school's timezone
 * @param {string} [schoolTimezone] IANA name
 * @param {string} parseFormat moment parse format describing localValue, e.g. "YYYY-MM-DD HH:mm"
 * @returns {import('moment').Moment|null} UTC-mode moment, or null for empty/invalid input
 */
export const toUtc = (localValue, schoolTimezone, parseFormat) => {
  if (localValue === null || localValue === undefined || localValue === '') return null
  const m = moment.tz(localValue, parseFormat, resolveTz(schoolTimezone))
  return m.isValid() ? m.utc() : null
}

/**
 * {@link toUtc} rendered as a UTC ISO string (e.g. "2026-06-16T18:00:00.000Z") for endpoints
 * that take an Instant.
 * @returns {string|null} UTC ISO string, or null for empty/invalid input
 */
export const toUtcIso = (localValue, schoolTimezone, parseFormat) => {
  const m = toUtc(localValue, schoolTimezone, parseFormat)
  return m ? m.toISOString() : null
}

/**
 * Midnight of a school-local calendar day as a zoneless UTC date-time, e.g. the Kyiv day 2026-10-08 ->
 * "2026-10-07T21:00:00". Bounds of date-window queries (schedule day/week, side menu): the backend filters
 * the half-open UTC range [from, to), so pass the first day and the day AFTER the last one.
 * @param {string|Date|import('moment').Moment} date the day; only its calendar date is used
 * @param {string} [schoolTimezone] IANA name
 * @returns {string|null} "YYYY-MM-DDTHH:mm:ss" in UTC, or null for empty/invalid input
 */
export const schoolDayStartUtc = (date, schoolTimezone) => {
  if (date === null || date === undefined || date === '') return null
  const day = moment(date)
  if (!day.isValid()) return null
  return toUtc(day.format('YYYY-MM-DD'), schoolTimezone, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss')
}

// Any "H:mm" / "HH:mm" clock token, regardless of the surrounding (localized) day text.
const TIME_TOKEN = /(\d{1,2}):(\d{2})/g
const normalizeHHmm = (h, m) => moment({ hour: Number(h), minute: Number(m) }).format('HH:mm')

/**
 * The stored group name embeds a school-tz-shifted time (a legacy write-side issue) — the day names do
 * not need fixing, only the times. This corrects just the numeric HH:mm tokens and leaves all day text
 * untouched, so it works for any localization (en / ua / rus) of the name.
 *
 * The group's lessons are served by the backend in UTC and are correct. For each lesson we compute its
 * true school-local time and the (shifted) value it appears as in the stored name — i.e. school-local
 * plus the school-tz offset for that lesson's own date — then replace any name token equal to that
 * shifted value with the true time. Keying by the shifted clock value (not the weekday) keeps it
 * language-agnostic, handles groups that meet at different times on different days, and is DST-safe
 * because each lesson contributes its own date's offset. Tokens with no match are left as-is.
 *
 * A name that already shows the school-local start time of any of the group's lessons is not shifted and is
 * returned unchanged: otherwise its end time could be taken for a shifted start - e.g. after DST ends
 * (+2) the end of a two-hour lesson, "11:45 - 13:45", would turn into "11:45 - 11:45".
 *
 * @param {string} name stored group name, e.g. "Fri 22:00 …" or "ВТ-ЧТ 16:00 …"
 * @param {Array<{startTime: string}>} lessons group lessons with UTC startTime
 * @param {string} [schoolTimezone] IANA name
 * @returns {string} name with each shifted HH:mm time corrected to the school-local lesson time
 */
export const correctGroupNameTimes = (name, lessons, schoolTimezone) => {
  if (!name || !Array.isArray(lessons) || !lessons.length) return name
  const nameTimes = new Set((name.match(TIME_TOKEN) || []).map(token => normalizeHHmm(...token.split(':'))))
  const correctByShifted = {}
  for (const lesson of lessons) {
    const local = toSchoolTz(lesson && lesson.startTime, schoolTimezone)
    if (!local) continue
    const correct = local.format('HH:mm')
    if (nameTimes.has(correct)) return name
    const shifted = moment(correct, 'HH:mm').add(local.utcOffset(), 'minutes').format('HH:mm')
    if (!(shifted in correctByShifted)) correctByShifted[shifted] = correct
  }
  if (!Object.keys(correctByShifted).length) return name
  return name.replace(TIME_TOKEN, (token, h, m) => correctByShifted[normalizeHHmm(h, m)] || token)
}
